/**
 * TypePickerModal — generic "pick the account variant before OAuth" UI.
 *
 * Used for platforms whose Connect flow can result in multiple kinds
 * of accounts (TikTok Business vs Personal, LinkedIn Personal vs
 * Company Page, YouTube Personal vs Brand, Pinterest Business vs
 * Personal). The user's choice is passed to the backend OAuth
 * init call as ``?type=business|personal|company|brand``, which the
 * backend signs into the OAuth state and honors on callback to set
 * ``SocialAccount.account_type`` authoritatively.
 *
 * Instagram keeps its own dedicated modal because it has three
 * variants (business_fb, business_ig, personal) with different OAuth
 * flows — not just a single "type" param.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Check,
  Briefcase,
  User,
  Building2,
  type LucideIcon,
} from 'lucide-react';

export interface TypePickerOption {
  /** Short value sent to the backend (e.g. ``business`` / ``company``). */
  key: string;
  /** Bold display name. */
  title: string;
  /** Short subtitle below the title. */
  subtitle: string;
  /** Longer explanation shown when the option is focused. */
  description?: string;
  /** Show a "Recommended" chip. */
  recommended?: boolean;
  /** Only one option per platform should be ``requiresAdmin`` — it
   * flags that the user needs admin role on a Page / Org and we'll
   * verify it server-side on callback. */
  requiresAdmin?: boolean;
  /** Icon intent used to pick a lucide icon at render time. */
  icon?: 'business' | 'personal' | 'company' | 'brand';
}

interface TypePickerModalProps {
  platformLabel: string;
  options: TypePickerOption[];
  /** Controlled selection key. */
  selectedKey: string;
  onSelect: (key: string) => void;
  onConfirm: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const ICON_FOR_INTENT: Record<
  NonNullable<TypePickerOption['icon']>,
  LucideIcon
> = {
  business: Briefcase,
  personal: User,
  company: Building2,
  brand: Briefcase,
};

export default function TypePickerModal({
  platformLabel,
  options,
  selectedKey,
  onSelect,
  onConfirm,
  onClose,
  isSubmitting = false,
}: TypePickerModalProps) {
  const { t } = useTranslation();

  return (
    <div
      className="n-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="type-picker-modal-title"
    >
      <div className="n-modal max-w-xl">
        <div className="n-modal-header">
          <h3
            id="type-picker-modal-title"
            className="text-[13px] font-semibold text-n-text"
          >
            {t('accounts_type_picker_title', 'Connect {{platform}} — pick account type', {
              platform: platformLabel,
            })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="n-btn-icon"
            aria-label={t('accounts_close_modal', 'Close')}
            disabled={isSubmitting}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="n-modal-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {options.map((opt) => {
              const isSelected = selectedKey === opt.key;
              const Icon = opt.icon ? ICON_FOR_INTENT[opt.icon] : User;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onSelect(opt.key)}
                  aria-pressed={isSelected}
                  disabled={isSubmitting}
                  className={`relative p-3 rounded-n border text-left transition-colors disabled:opacity-50 ${
                    isSelected
                      ? 'border-n-blue bg-n-blue-bg'
                      : 'border-n-border bg-n-card hover:bg-n-hover'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-n-blue text-white flex items-center justify-center">
                      <Check className="w-2.5 h-2.5" strokeWidth={3} />
                    </div>
                  )}
                  {opt.recommended && (
                    <span className="inline-block mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-n-green bg-n-green-bg px-1.5 py-0.5 rounded-n-xs">
                      {t('accounts_recommended_badge', 'Recommended')}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Icon size={14} className="text-n-text-lt" />
                    <h4 className="text-[12px] font-semibold text-n-text">
                      {opt.title}
                    </h4>
                  </div>
                  <p className="text-[11px] text-n-text-md mt-0.5">{opt.subtitle}</p>
                  {opt.description && (
                    <p className="text-[10px] text-n-text-lt mt-1.5 leading-relaxed">
                      {opt.description}
                    </p>
                  )}
                  {opt.requiresAdmin && (
                    <p className="text-[10px] text-n-amber mt-1.5">
                      {t(
                        'accounts_type_picker_requires_admin',
                        'Requires admin role — we verify on connect.',
                      )}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="n-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="n-btn n-btn-secondary"
            disabled={isSubmitting}
          >
            {t('accounts_cancel_btn', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="n-btn n-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('accounts_connecting_btn', 'Connecting…')
              : t('accounts_continue_btn', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Per-platform option catalogs. Platforms not in this map fall back
 * to the default OAuth flow without a picker (the user just clicks
 * Connect and the callback assigns a default ``account_type``). */
export const TYPE_PICKER_OPTIONS: Record<string, TypePickerOption[]> = {
  tiktok: [
    {
      key: 'business',
      title: 'Business',
      subtitle: 'TikTok for Business',
      description: 'Publishing + analytics APIs. Required for scheduled posts.',
      icon: 'business',
      recommended: true,
    },
    {
      key: 'personal',
      title: 'Personal',
      subtitle: 'Creator / personal account',
      description: 'Manual posting only — TikTok API limits apply.',
      icon: 'personal',
    },
  ],
  linkedin: [
    {
      key: 'personal',
      title: 'Personal Profile',
      subtitle: 'Your LinkedIn identity',
      description: 'Post from your own profile.',
      icon: 'personal',
    },
    {
      key: 'company',
      title: 'Company Page',
      subtitle: 'A LinkedIn organization',
      description:
        'Post as a Company. We verify you have admin rights before connecting.',
      icon: 'company',
      requiresAdmin: true,
      recommended: true,
    },
  ],
  youtube: [
    {
      key: 'personal',
      title: 'Personal Channel',
      subtitle: 'Your primary YouTube channel',
      icon: 'personal',
    },
    {
      key: 'brand',
      title: 'Brand Account',
      subtitle: 'A brand / business YouTube channel',
      description: 'Managed separately from your personal Google account.',
      icon: 'brand',
      recommended: true,
    },
  ],
  pinterest: [
    {
      key: 'business',
      title: 'Business',
      subtitle: 'Pinterest Business account',
      description: 'Unlocks analytics + API publishing.',
      icon: 'business',
      recommended: true,
    },
    {
      key: 'personal',
      title: 'Personal',
      subtitle: 'Regular Pinner account',
      icon: 'personal',
    },
  ],
};
